
import { RegistrationFormData } from './registrationSchema';
import { getCourseNameInArmenian, getLearningTypeInArmenian, getLearningFormatInArmenian, getDurationInArmenian } from './translations';
import { supabase } from '@/integrations/supabase/client';

export const sendToTelegram = async (data: RegistrationFormData) => {
  const startTime = Date.now();
  console.log('🔄 Starting Telegram notification process...', {
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    course: data.course,
    timestamp: new Date().toISOString()
  });

  try {
    const courseNameArmenian = getCourseNameInArmenian(data.course);
    const learningTypeArmenian = getLearningTypeInArmenian(data.learningType);
    const learningFormatArmenian = getLearningFormatInArmenian(data.learningFormat);
    const durationArmenian = getDurationInArmenian(data.duration);
    
    // Add pricing information based on learning type (from live settings)
    let groupPriceValue = 60000;
    let individualPriceValue = 90000;
    try {
      const { data: priceSettings } = await supabase
        .from('settings')
        .select('key, value')
        .in('key', ['group_price', 'individual_price']);
      priceSettings?.forEach((s: { key: string; value: string }) => {
        const parsed = parseInt(s.value, 10);
        if (!Number.isNaN(parsed)) {
          if (s.key === 'group_price') groupPriceValue = parsed;
          if (s.key === 'individual_price') individualPriceValue = parsed;
        }
      });
    } catch (priceError) {
      console.warn('⚠️ Could not load prices from settings, using defaults', priceError);
    }

    const formatAmd = (value: number) => value.toLocaleString('en-US').replace(/,/g, '․');
    const monthlyPrice = `${formatAmd(data.learningType === 'group' ? groupPriceValue : individualPriceValue)}դր/ամիսը`;

    
    let specializationText = '';
    if (data.specialization) {
      specializationText = `📋 Մասնագիտացում: ${data.specialization}\n`;
    }
    
    let languageText = '';
    if (data.language) {
      languageText = `💻 Ծրագրավորման լեզու: ${data.language}\n`;
    }
    
    let facultyText = '';
    if (data.faculty) {
      facultyText = `🏛️ Ֆակուլտետ: ${data.faculty}\n`;
    }
    
    const message = `
🎓 Նոր դիմում Global IT Academy-ից

👤 Անուն: ${data.firstName}
👤 Ազգանուն: ${data.lastName}
📞 Հեռախոսահամար: ${data.phone}
✉️ Էլ. փոստ: ${data.email}
${facultyText}📚 Ընտրված դասընթաց: ${courseNameArmenian}
${specializationText}${languageText}📖 Ուսուցման տեսակ: ${learningTypeArmenian}
📖 Ուսուցման ձև: ${learningFormatArmenian}
⏰ Տևողություն: ${durationArmenian}
💰 Ամսական արժեք (դիմումը հաստատվելու դեպքում): ${monthlyPrice}

📅 Դիմման ամսաթիվ: ${new Date().toLocaleString('hy-AM')}
    `.trim();

    console.log('📝 Message prepared for Telegram:', {
      messageLength: message.length,
      recipientType: 'all',
      courseNameArmenian,
      learningTypeArmenian
    });
    
    // Use the send-telegram-message edge function instead of direct API call
    console.log('🚀 Invoking send-telegram-message edge function...');
    console.log('🚀 Supabase client created successfully');
    console.log('🚀 Edge function body:', {
      message: message.substring(0, 100) + '...',
      recipientType: 'all',
      messageLength: message.length
    });
    
    const { data: result, error } = await supabase.functions.invoke('notify-registration-telegram', {
      body: {
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        email: data.email,
        course: courseNameArmenian,
        faculty: data.faculty,
        specialization: data.specialization,
        language: data.language,
        learningType: learningTypeArmenian,
        learningFormat: learningFormatArmenian,
        duration: durationArmenian,
      }
    });


    console.log('🚀 Edge function response:', {
      data: result,
      error: error,
      hasData: !!result,
      hasError: !!error
    });

    const executionTime = Date.now() - startTime;

    if (error) {
      console.error('❌ Telegram edge function error:', {
        error: error,
        errorMessage: error.message,
        errorCode: error.code,
        executionTime: `${executionTime}ms`,
        userEmail: data.email
      });
      
      // Log to database for debugging (optional - could be added later)
      try {
        await supabase.from('telegram_messages').insert({
          message_text: message.substring(0, 1000), // Truncate if too long
          recipient_type: 'all',
          status: 'failed',
          error_message: error.message || 'Unknown error',
          sent_by: null // No authenticated user for public registrations
        });
      } catch (dbError) {
        console.warn('⚠️ Failed to log Telegram error to database:', dbError);
      }
      
      throw new Error(`Telegram notification failed: ${error.message || 'Unknown error'}`);
    }

    console.log('✅ Telegram message sent successfully:', {
      result: result,
      executionTime: `${executionTime}ms`,
      userEmail: data.email,
      success: result?.success,
      summary: result?.summary
    });

      // Log successful message to database
      try {
        await supabase.from('telegram_messages').insert({
          message_text: message.substring(0, 1000), // Truncate if too long
          recipient_type: 'all',
          status: 'sent',
          sent_by: null // No authenticated user for public registrations
        });
        console.log('📊 Telegram message logged to database');
      } catch (dbError) {
        console.warn('⚠️ Failed to log successful Telegram message to database:', dbError);
      }

    return result;
  } catch (error: any) {
    const executionTime = Date.now() - startTime;
    
    console.error('💥 Critical error in sendToTelegram:', {
      error: error,
      errorMessage: error.message,
      errorStack: error.stack,
      executionTime: `${executionTime}ms`,
      userEmail: data.email,
      userCourse: data.course
    });
    
    // Categorize error types for better handling
    if (error.message?.includes('fetch')) {
      throw new Error('Network error: Unable to connect to Telegram service');
    } else if (error.message?.includes('timeout')) {
      throw new Error('Timeout error: Telegram service took too long to respond');
    } else if (error.message?.includes('not configured')) {
      throw new Error('Configuration error: Telegram bot is not properly set up');
    } else {
      throw new Error(`Telegram notification error: ${error.message || 'Unknown error occurred'}`);
    }
  }
};
